// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import {
  MapPin,
  ExternalLink,
  X,
  ChevronDown,
  Search,
  Share2,
  ClipboardPaste,
  Loader2,
} from "lucide-react";
import { Button, FormField, Input, Select, useToast } from "@chotca/ui";
import { ChotcaApiError } from "@chotca/sdk";
import {
  type ProvinceCode,
  type GeocodeSuggestion,
  type GeocodeParsedAddress,
  VN_PROVINCES_2025,
  getWardsForProvince2025,
} from "@chotca/config";
import { getSdk } from "@chotca/api-client";

// AddressPicker — two ways to set the work address:
//
//   A. Type-to-search (primary). Google Places (New) autocomplete, proxied
//      through our API so the key never hits the browser and keystrokes +
//      the final details lookup bill as one session. Pick a suggestion and
//      address / district / city / GPS auto-fill.
//   B. Paste a Google Maps share link (fallback). Free, exact — the URL itself
//      encodes lat/lng + address; the server expands short links and parses
//      `/place/<addr>/@<lat>,<lng>`. Kept for when a place isn't in Places or
//      when the Places key isn't configured (server returns a clean 503 and we
//      just show the paste box).
//
// Both paths converge on the same GeocodeParsedAddress shape → one applyParsed()
// handler fills the form. Lat/lng come straight from Google either way, so we
// trust them as-is — no map preview / drag pin.

export type AddressValue = {
  address: string;
  // 2025 post-merger admin unit. `wardProvinceCode` is the official NEW numeric
  // province code as string ("79" = HCM); `wardCode`/`wardName` the phường/xã.
  // Auto-filled server-side by point-in-polygon and editable by the user.
  wardCode: string;
  wardName: string;
  wardProvinceCode: string;
  lat: number | null;
  lng: number | null;
  // Legacy admin fields — no longer shown/edited, kept so the write path can
  // satisfy the (still NOT NULL) district/city columns during the transition.
  district: string;
  city: string;
  provinceCode: ProvinceCode | null;
  provinceName: string | null;
  // Canonical Google Maps URL captured from the geocoder. Persisted on
  // OrgAddress so worker mobile "Open Maps" deep-links to the branded pin
  // (place name, hours, photos) instead of the bare lat/lng.
  mapUrl: string | null;
};

export type AddressPickerProps = {
  label?: React.ReactNode;
  description?: React.ReactNode;
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  required?: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
};

export function AddressPicker({
  label,
  description,
  value,
  onChange,
  required,
  disabled,
  error,
}: AddressPickerProps) {
  const { toast } = useToast();
  const [pasted, setPasted] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  // Turn a server-parsed address (from either autocomplete details OR a pasted
  // Maps link) into the form value. Shared so both entry paths behave alike.
  function applyParsed(r: GeocodeParsedAddress, fallbackMapUrl: string | null) {
    // Ward is auto-resolved server-side (point-in-polygon on lat/lng). It may
    // be null if the pin fell outside every polygon — the user then picks the
    // ward manually from the dropdown.
    onChange({
      address: r.address,
      wardCode: r.wardCode ?? "",
      wardName: r.wardName ?? "",
      wardProvinceCode: r.wardProvinceCode ?? "",
      lat: r.lat,
      lng: r.lng,
      // Legacy fields carried through only to satisfy the write path.
      district: r.district ?? "",
      city: r.city ?? "",
      provinceCode: r.provinceCode,
      provinceName: r.provinceName,
      // Canonical Google URL if the server resolved one, else the raw input.
      mapUrl: r.resolvedUrl ?? fallbackMapUrl,
    });
  }

  async function applyUrl() {
    const url = pasted.trim();
    if (!url) return;
    setBusy(true);
    try {
      const r = await getSdk().geocode.parseUrl(url);
      applyParsed(r, url);
      setPasted("");
      toast({ tone: "success", title: "Đã lấy địa điểm từ Google Maps" });
    } catch (err) {
      const msg =
        err instanceof ChotcaApiError && err.message
          ? err.message
          : "Không nhận diện được link. Hãy thử dán link gốc từ Google Maps.";
      toast({
        tone: "error",
        title: "Không lấy được vị trí",
        description: msg,
      });
    } finally {
      setBusy(false);
    }
  }

  function clearAll() {
    onChange({
      address: "",
      wardCode: "",
      wardName: "",
      wardProvinceCode: "",
      district: "",
      city: "",
      provinceCode: null,
      provinceName: null,
      lat: null,
      lng: null,
      mapUrl: null,
    });
  }

  const hasValue = !!value.address;
  // Wards for the currently-selected province (official 2025 catalog).
  const wardOptions = React.useMemo(
    () =>
      getWardsForProvince2025(value.wardProvinceCode).map((w) => ({
        value: w.c,
        label: w.n,
      })),
    [value.wardProvinceCode],
  );

  return (
    <div className="space-y-3">
      <FormField
        label={label ?? "Địa chỉ làm việc"}
        description={description}
        error={error}
        optional={!required}
      >
        {!disabled ? (
          <>
            {/* Primary: type-to-search via Google Places autocomplete. */}
            <AddressSearch
              onPick={(parsed) => {
                applyParsed(parsed, parsed.resolvedUrl);
                toast({ tone: "success", title: "Đã chọn địa điểm" });
              }}
            />

            {/* Fallback: paste a Google Maps share link. Collapsed by default —
                most users won't need it now that search exists. */}
            <PasteLinkFallback
              pasted={pasted}
              setPasted={setPasted}
              busy={busy}
              onApply={() => void applyUrl()}
            />
          </>
        ) : null}
      </FormField>

      {hasValue ? (
        <div className="space-y-3 rounded-lg border border-smoke bg-mist/40 p-3">
          <div className="flex items-start gap-2">
            <MapPin size={16} className="mt-0.5 shrink-0 text-ink" />
            <p className="flex-1 text-xs text-ash">
              Hệ thống đã điền sẵn từ Google Maps. Bạn có thể chỉnh lại nếu chưa
              chính xác.
            </p>
            {!disabled ? (
              <button
                type="button"
                onClick={clearAll}
                aria-label="Xoá địa chỉ"
                className="p-1 text-ash hover:text-ink"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>

          <div className="space-y-2">
            <Input
              value={value.address}
              onChange={(e) => onChange({ ...value, address: e.target.value })}
              placeholder="Số nhà, tên đường, phường"
              disabled={disabled}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={
                  wardOptions.some((o) => o.value === (value.wardCode || ""))
                    ? value.wardCode
                    : ""
                }
                onValueChange={(code) => {
                  const w = wardOptions.find((o) => o.value === code);
                  onChange({
                    ...value,
                    wardCode: code,
                    wardName: w?.label ?? "",
                  });
                }}
                placeholder={
                  value.wardProvinceCode
                    ? "Phường / xã"
                    : "Chọn tỉnh / thành trước"
                }
                options={wardOptions}
                invalid={!value.wardCode}
                disabled={disabled || !value.wardProvinceCode}
                className="w-full"
              />
              <Select
                value={value.wardProvinceCode ?? ""}
                onValueChange={(code) => {
                  const p = VN_PROVINCES_2025.find((sp) => sp.code === code);
                  onChange({
                    ...value,
                    wardProvinceCode: p?.code ?? "",
                    provinceName: p?.name ?? null,
                    // Changing province invalidates the ward selection.
                    wardCode:
                      p?.code === value.wardProvinceCode ? value.wardCode : "",
                    wardName:
                      p?.code === value.wardProvinceCode ? value.wardName : "",
                  });
                }}
                placeholder="Tỉnh / thành phố"
                options={VN_PROVINCES_2025.map((p) => ({
                  value: p.code,
                  label: p.name,
                }))}
                invalid={!value.wardProvinceCode}
                disabled={disabled}
                className="w-full"
              />
            </div>
          </div>

          {!value.wardProvinceCode || !value.wardCode ? (
            <p className="text-xs text-warning">
              Vui lòng chọn đầy đủ tỉnh / thành và phường / xã.
            </p>
          ) : null}

          {value.lat !== null && value.lng !== null ? (
            <p className="font-mono text-xs text-ash">
              GPS {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// AddressSearch — type-to-search box backed by Google Places autocomplete.
// Owns its own transient state (query text, suggestions, open/loading, and the
// per-search session token) so the parent only hears about a final pick.
function AddressSearch({
  onPick,
}: {
  onPick: (parsed: GeocodeParsedAddress) => void;
}) {
  const { toast } = useToast();
  const [query, setQuery] = React.useState("");
  const [suggestions, setSuggestions] = React.useState<GeocodeSuggestion[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);
  // One session token per search — regenerated after a pick (or an abandoned
  // search) so Google bills each completed lookup as a single session.
  const sessionRef = React.useRef<string>(newSessionToken());
  const boxRef = React.useRef<HTMLDivElement>(null);
  // Guards against a slow autocomplete response overwriting a newer query's
  // results (out-of-order responses).
  const reqIdRef = React.useRef(0);

  // Debounced fetch: wait 400ms after the last keystroke before calling.
  React.useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    const t = setTimeout(async () => {
      try {
        const r = await getSdk().geocode.autocomplete(q, sessionRef.current);
        if (reqIdRef.current !== myReq) return; // a newer keystroke won
        setSuggestions(r.suggestions);
        setOpen(true);
      } catch {
        // Places not configured / transient error — stay quiet, the paste-link
        // fallback below is always available.
        if (reqIdRef.current === myReq) setSuggestions([]);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  // Close the dropdown on an outside click.
  React.useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  async function pick(s: GeocodeSuggestion) {
    setOpen(false);
    setResolving(true);
    try {
      const parsed = await getSdk().geocode.placeDetails(
        s.placeId,
        sessionRef.current,
      );
      onPick(parsed);
      setQuery("");
      setSuggestions([]);
    } catch (err) {
      const msg =
        err instanceof ChotcaApiError && err.message
          ? err.message
          : "Không lấy được chi tiết địa điểm. Thử lại hoặc dán link Google Maps.";
      toast({
        tone: "error",
        title: "Không lấy được vị trí",
        description: msg,
      });
    } finally {
      setResolving(false);
      // New billing session for the next search.
      sessionRef.current = newSessionToken();
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ash"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (suggestions.length) setOpen(true);
          }}
          placeholder="Tìm địa chỉ (vd: 395 Bùi Đình Túy, Bình Thạnh)…"
          className="pl-9 pr-9"
          disabled={resolving}
        />
        {loading || resolving ? (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-ash"
          />
        ) : null}
      </div>

      {open && suggestions.length > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-smoke bg-paper py-1 shadow-lg">
          {suggestions.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                onClick={() => void pick(s)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-mist"
              >
                <MapPin size={15} className="mt-0.5 shrink-0 text-ash" />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink">
                    {s.primaryText}
                  </span>
                  {s.secondaryText ? (
                    <span className="block truncate text-xs text-ash">
                      {s.secondaryText}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// PasteLinkFallback — the original "paste a Google Maps share link" flow,
// collapsed by default. Still the escape hatch when Places has no match or the
// Places key isn't configured server-side.
function PasteLinkFallback({
  pasted,
  setPasted,
  busy,
  onApply,
}: {
  pasted: string;
  setPasted: (v: string) => void;
  busy: boolean;
  onApply: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs text-ash hover:text-ink"
      >
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
        Hoặc dán link Google Maps
      </button>

      {open ? (
        <div className="mt-2 space-y-2">
          <Instructions />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="https://maps.app.goo.gl/… hoặc https://www.google.com/maps/place/…"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onApply();
                }
              }}
            />
            <Button
              type="button"
              variant="primary"
              loading={busy}
              disabled={!pasted.trim()}
              onClick={onApply}
            >
              Lấy địa điểm
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// crypto.randomUUID is available in every browser the employer app targets.
function newSessionToken(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Non-crypto fallback (extremely old browsers) — session tokens only need to
  // be unique per search, not cryptographically strong.
  return `s-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

// Instruction strip — keeps the user from getting lost on first use. Three
// visual steps with icons + a tiny URL hint so they recognise the right format.
function Instructions() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-smoke bg-mist/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-mist"
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ink text-paper">
          <MapPin size={14} />
        </span>
        <span className="flex-1 text-sm font-semibold text-ink">
          Cách lấy link địa điểm từ Google Maps
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-ash transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="border-t border-smoke bg-paper px-3.5 py-4">
          <ol className="grid gap-3 sm:grid-cols-3">
            <Step
              n={1}
              icon={<Search size={14} />}
              title={
                <>
                  Mở{" "}
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 font-semibold text-info underline-offset-2 hover:underline"
                  >
                    Google Maps
                    <ExternalLink size={11} />
                  </a>
                </>
              }
              body="Tìm đúng địa chỉ làm việc trên web hoặc app."
            />
            <Step
              n={2}
              icon={<Share2 size={14} />}
              title={
                <>
                  Bấm <strong className="font-semibold">Chia sẻ</strong> →{" "}
                  <strong className="font-semibold">Sao chép liên kết</strong>
                </>
              }
              body="Icon mũi tên trên thẻ địa điểm."
            />
            <Step
              n={3}
              icon={<ClipboardPaste size={14} />}
              title={
                <>
                  Dán &amp; bấm{" "}
                  <strong className="font-semibold">Lấy địa điểm</strong>
                </>
              }
              body="Hệ thống tự điền tên đường, quận, thành phố và toạ độ GPS."
            />
          </ol>

          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ash">
            <span>Link hợp lệ:</span>
            <code className="rounded bg-mist px-1.5 py-0.5 font-mono text-[11px] text-ink/70">
              maps.app.goo.gl/…
            </code>
            <span className="text-ash/70">hoặc</span>
            <code className="rounded bg-mist px-1.5 py-0.5 font-mono text-[11px] text-ink/70">
              google.com/maps/place/…
            </code>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  body,
}: {
  n: number;
  icon: React.ReactNode;
  title: React.ReactNode;
  body: React.ReactNode;
}) {
  return (
    <li className="relative rounded-lg border border-smoke bg-mist/30 p-3 pl-3">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink text-[11px] font-semibold text-paper">
          {n}
        </span>
        <span className="text-ash">{icon}</span>
      </div>
      <p className="text-sm leading-snug text-ink">{title}</p>
      <p className="mt-1 text-xs leading-snug text-ash">{body}</p>
    </li>
  );
}
